import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  Container,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Lock,
  PersonAdd,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { register, clearError } from '../../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';

const validationSchema = Yup.object({
  name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .required('Name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  phoneNumber: Yup.string()
    .matches(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number')
    .required('Phone number is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error } = useSelector((state: RootState) => state.auth);

  // Clear any stale errors when navigating to the register page
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await dispatch(register(values)).unwrap();
        navigate('/verify-email');
      } catch (error) {
        // Error handled by Redux
      }
    },
  });

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      background: 'linear-gradient(135deg, #F8F8FC 0%, #E8F4FD 100%)',
    }}>
      {/* Left decorative panel */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flex: 1,
        background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 6,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -60, left: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -80, right: -40, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <Box
          component="img"
          src="https://mal-pay.com/images/logo22.png"
          alt="MalPay"
          sx={{ height: 60, mb: 3, filter: 'brightness(10)' }}
          onError={(e: any) => { e.target.style.display = 'none'; }}
        />
        <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 2, textAlign: 'center' }}>
          Join MalPay Today
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', maxWidth: 400, lineHeight: 1.8 }}>
          Send and receive money with ease. Create your free account in minutes.
        </Typography>
      </Box>

      {/* Right form */}
      <Box sx={{
        flex: { xs: 1, md: '0 0 560px' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 3, sm: 6 },
        overflowY: 'auto',
      }}>
        <Container maxWidth="sm" sx={{ width: '100%', maxWidth: 460 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 3 }}>
            <Box
              component="img"
              src="https://mal-pay.com/images/logo22.png"
              alt="MalPay"
              sx={{ height: 50 }}
              onError={(e: any) => { e.target.style.display = 'none'; }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#202647', mb: 1 }}>
              Create Account
            </Typography>
            <Typography variant="body1" sx={{ color: '#7F839C' }}>
              Fill in your details to get started
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }} onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#272A3A', mb: 0.5 }}>Full Name</Typography>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  placeholder="John Doe"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Person sx={{ color: '#B0B0C0' }} /></InputAdornment>,
                  }}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#272A3A', mb: 0.5 }}>Email Address</Typography>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  placeholder="you@example.com"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Email sx={{ color: '#B0B0C0' }} /></InputAdornment>,
                  }}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#272A3A', mb: 0.5 }}>Phone Number</Typography>
                <TextField
                  fullWidth
                  id="phoneNumber"
                  name="phoneNumber"
                  placeholder="+234..."
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                  helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Phone sx={{ color: '#B0B0C0' }} /></InputAdornment>,
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#272A3A', mb: 0.5 }}>Password</Typography>
                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    placeholder="Min 8 characters"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Lock sx={{ color: '#B0B0C0' }} /></InputAdornment>,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#272A3A', mb: 0.5 }}>Confirm</Typography>
                  <TextField
                    fullWidth
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder="Re-enter password"
                    type="password"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                    helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Lock sx={{ color: '#B0B0C0' }} /></InputAdornment>,
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              startIcon={!isLoading && <PersonAdd />}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 700,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
                boxShadow: '0 4px 15px rgba(17, 193, 236, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0EAED6 0%, #07395A 100%)',
                  boxShadow: '0 6px 20px rgba(17, 193, 236, 0.4)',
                },
              }}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#7F839C' }}>
                Already have an account?{' '}
                <Link
                  component={RouterLink}
                  to="/login"
                  variant="body2"
                  sx={{ color: '#11C1EC', fontWeight: 700, textDecoration: 'none', '&:hover': { color: '#09486D' } }}
                >
                  Sign in here
                </Link>
              </Typography>
            </Box>
          </form>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#B0B0C0', fontSize: '0.8rem' }}>
              By creating an account, you agree to our Terms of Service and Privacy Policy
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default RegisterPage;
